import { useEffect, useState, type ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import { useInView } from 'react-intersection-observer';
import { useLocalisation } from '@archipro-website/localisation';
import { uiStateAtomReachedFooter } from '@rocco/ui/website-footer/states/ui-states-reached-footer';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as ArchiproWordmark } from '@rocco/icons/svg/logo-text.svg';
import { ReactComponent as CCPAOptOutIcon } from '@rocco/icons/svg/ccpa-opt-out.svg';
import { SubscribePublicationsTrigger } from '@modules-rocco/subscription/component/SubscribePublicationsTrigger';
import { SubscribeField } from '../components/SubscribeField';
import { useFeature } from '~/modules/root';
import { ProductLaunchCountdown } from '~/ui-rocco/components/product-launch-countdown/ProductLaunchCountdown';
import { useProductLaunchCountdownEnabled } from '~/ui-rocco/components/product-launch-countdown/use-product-launch-countdown-enabled';
import { isUS } from '~/modules/root/page/us-footer-gating';
import { placeCountryDomain } from '~/utils/urlHelper';
import { getDomain } from '~/utils/get-domain';
import {
    FOOTER_WHY_ARCHIPRO_LINKS,
    FOOTER_SOLUTIONS_LINKS,
    FOOTER_MEMBERSHIP_LINKS,
    FOOTER_RESOURCES_LINKS,
    PagesEnum,
    getWebsiteTermsLink,
    getPrivacyPolicyLink,
} from '~/utils/route-config';
import { useMatchesHandleData } from '~/modules/root/hook/use-matches-handle-data';

/**
 * Site-wide footer (Figma `997:34898` desktop, `1126:32449` mobile).
 *
 * Desktop: subscribe block + link groups (Discover/Business and ArchiPro/Connect)
 * separated by vertical rules. Mobile: subscribe block above a single-column
 * accordion. Shares the ARCHIPRO wordmark, copyright and legal links along the
 * bottom. Rendered globally by `AppPage` (replacing the legacy footer), gated
 * by `footerNavigation` so footer-hidden routes still hide it.
 *
 * Country gating mirrors the legacy footer / top nav (BC-4427):
 *  - US: hide Digital Magazines from Discover.
 *  - Home Design Evening follows the top nav (`countryConfig.showHDENavLink`).
 *  - Business links resolve to the per-market business subdomain.
 *
 * `theme` follows the header's variant pattern. Defaults to `dark`; `light`
 * (b2b palette) is supported for later opt-in but not used by default.
 */
type FooterTheme = 'dark' | 'light';

interface FooterThemeTokens {
    /**
     * Root background + base text, plus the focus-ring flip: the footer's dark
     * ground needs the off-white ring, not the near-black one the rest of the
     * page uses. The token inherits, so every focusable child follows.
     */
    section: string;
    /** Primary link text. */
    text: string;
    /** Group titles, copyright, legal. */
    muted: string;
    /** Divider rules (border colour). */
    rule: string;
    /** Wordmark fill. */
    wordmark: string;
    /** Background of drawn glyphs (the accordion's plus/minus bars). */
    glyph: string;
}

const THEME: Record<FooterTheme, FooterThemeTokens> = {
    dark: {
        section: 'bg-[#161617] text-offwhite focus-ring-on-dark',
        text: 'text-offwhite',
        muted: 'text-offwhite/50',
        rule: 'border-offwhite/15',
        wordmark: 'fill-offwhite',
        glyph: 'bg-offwhite',
    },
    light: {
        section: 'bg-[#fafafa] text-[#141414] focus-ring-on-light',
        text: 'text-[#141414]',
        muted: 'text-[#141414]/50',
        rule: 'border-black/10',
        wordmark: 'fill-[#141414]',
        glyph: 'bg-[#141414]',
    },
};

interface FooterLink {
    label: string;
    to: string;
    newTab?: boolean;
    isNonRemixRoute?: boolean;
    /** Rendered inside the link, after the label. */
    icon?: ReactNode;
}
interface FooterColumn {
    title: string;
    links: FooterLink[];
}

/**
 * Privacy opt-out link, shown in every market in the desktop bottom bar and the
 * mobile Privacy accordion. In the US the California opt-out-link rules require
 * this exact title with the opt-out icon adjacent to it, so there the label and
 * the icon stay together. The icon is a California symbol with no meaning in
 * other markets, so they get the label alone. It is drawn in `currentColor` and
 * its cross is a cut-out rather than a painted shape, so it reads correctly
 * against either footer theme. The label sits next to it, so the icon is
 * decorative to assistive tech.
 */
const YOUR_PRIVACY_CHOICES_LABEL = 'Your Privacy Choices';

// Sized against the surrounding text so it stays in proportion with the rest of
// the footer at both breakpoints. The icon carries a unit of padding inside its
// viewBox, so `1em` here draws a pill of `0.875em`. The footer renders it only a
// few pixels tall, where an SVG viewport rounded down to the nearest device pixel
// shaves the edge of the artwork, so the padding plus `overflow-visible` keep the
// pill off the clipping boundary.
const OptOutIcon = () => (
    <CCPAOptOutIcon
        aria-hidden="true"
        className="h-[1em] w-auto overflow-visible"
    />
);

const yourPrivacyChoicesLink = (us: boolean): FooterLink => ({
    label: YOUR_PRIVACY_CHOICES_LABEL,
    to: PagesEnum.YOUR_PRIVACY_CHOICES,
    ...(us ? { icon: <OptOutIcon /> } : {}),
});

const DISCOVER: FooterColumn = {
    title: 'Discover',
    links: [
        { label: 'Projects', to: PagesEnum.PROJECTS },
        { label: 'Products', to: PagesEnum.PRODUCTS },
        { label: 'Professionals', to: PagesEnum.PROFESSIONALS },
        { label: 'Archizeen', to: PagesEnum.ARCHIZEEN },
        { label: 'Digital Magazines', to: PagesEnum.DIGITAL_MAGAZINES },
    ],
};

// FAQ removed for all countries (BC-4427).
const ARCHIPRO: FooterColumn = {
    title: 'ArchiPro',
    links: [
        { label: 'About', to: PagesEnum.ABOUT },
        { label: 'Careers', to: '/careers' },
        { label: 'Contact', to: PagesEnum.CONTACT, isNonRemixRoute: true },
    ],
};

const CONNECT: FooterColumn = {
    title: 'Connect',
    links: [
        { label: 'Home Design Evening', to: PagesEnum.HOME_DESIGN_EVENING },
        {
            label: 'Instagram',
            to: PagesEnum.INSTAGRAM,
            newTab: true,
            isNonRemixRoute: true,
        },
        {
            label: 'Facebook',
            to: PagesEnum.FACEBOOK,
            newTab: true,
            isNonRemixRoute: true,
        },
        {
            label: 'Linkedin',
            to: PagesEnum.LINKEDIN,
            newTab: true,
            isNonRemixRoute: true,
        },
        {
            label: 'YouTube',
            to: PagesEnum.YOUTUBE,
            newTab: true,
            isNonRemixRoute: true,
        },
        {
            label: 'Pinterest',
            to: PagesEnum.PINTEREST,
            newTab: true,
            isNonRemixRoute: true,
        },
    ],
};

/**
 * Slim Business group for the US market: the two entry points to the Webflow
 * B2B microsite on the per-market business subdomain, resolved by
 * `placeCountryDomain`. NZ/AU show the full set via `businessGroupsForCountry`.
 */
const BUSINESS_TEMPLATE = {
    title: 'Business',
    links: [
        {
            title: 'Become a Pro',
            to: PagesEnum.FOR_PROFESSIONALS,
            newTab: true,
        },
        {
            title: 'For Brands',
            to: PagesEnum.FOR_SUPPLIERS_AND_BRANDS,
            newTab: true,
        },
    ],
};

const businessColumn = (countryCode: string): FooterColumn => {
    const resolved = placeCountryDomain(
        BUSINESS_TEMPLATE,
        getDomain(countryCode as Parameters<typeof getDomain>[0]),
    );
    return {
        title: resolved.title,
        links: resolved.links.map((link) => ({
            label: link.title,
            to: link.to,
            newTab: link.newTab,
            isNonRemixRoute: true,
        })),
    };
};

/**
 * Full "For Businesses" groups for non-US markets, reusing the legacy footer
 * config so NZ/AU keep the complete set (Why ArchiPro? / Business Solutions /
 * Memberships / Resources). US uses the slim {@link businessColumn} instead.
 */
const businessGroupsForCountry = (countryCode: string): FooterColumn[] => {
    const domain = getDomain(countryCode as Parameters<typeof getDomain>[0]);
    const toColumn = (
        group: Parameters<typeof placeCountryDomain>[0],
    ): FooterColumn => {
        const resolved = placeCountryDomain(group, domain);
        return {
            title: resolved.title,
            links: resolved.links.map((link) => ({
                label: link.title,
                to: link.to,
                // Legacy business/marketing pages open in a new tab via a full
                // document navigation (they're not Remix routes).
                newTab: link.newTab ?? true,
                isNonRemixRoute: link.isNonRemixRoute ?? true,
            })),
        };
    };
    return [
        toColumn(FOOTER_WHY_ARCHIPRO_LINKS),
        toColumn(FOOTER_SOLUTIONS_LINKS),
        toColumn(FOOTER_MEMBERSHIP_LINKS),
        toColumn(FOOTER_RESOURCES_LINKS),
    ];
};

const LinkColumn = ({
    title,
    links,
    t,
}: FooterColumn & { t: FooterThemeTokens }) => (
    <div className="flex flex-col gap-5">
        <p className={cn('m-0 text-base leading-5 font-normal', t.muted)}>
            {title}
        </p>
        <div className="flex flex-col gap-2.5">
            {links.map((link) => (
                <RoccoLink
                    key={link.label}
                    to={link.to}
                    newTab={link.newTab}
                    isNonRemixRoute={link.isNonRemixRoute}
                    className={cn(
                        'text-base leading-5 font-medium transition-opacity hover:opacity-70',
                        link.icon && 'inline-flex items-center gap-2',
                        t.text,
                    )}
                >
                    {link.label}
                    {link.icon}
                </RoccoLink>
            ))}
        </div>
    </div>
);

const SubscribeBlock = ({ t }: { t: FooterThemeTokens }) => {
    const canChoosePublications = useFeature('revamp_2026');

    return (
        <>
            <p
                className={cn(
                    'm-0 leading-[1.3] font-normal tracking-[0.02em] max-md:text-[3.38vw] md:text-base',
                    t.text,
                )}
            >
                Stay informed about the newest projects, products, and
                technologies shaping the future of the architecture and design
                environment.
            </p>
            {canChoosePublications ? (
                <SubscribePublicationsTrigger
                    variant="footer"
                    className="mt-2"
                />
            ) : (
                <SubscribeField variant="footer" className="mt-2" />
            )}
        </>
    );
};

/** Collapsible link groups for the mobile footer. */
const FooterAccordion = ({
    groups,
    t,
}: {
    groups: FooterColumn[];
    t: FooterThemeTokens;
}) => {
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
        Discover: true,
    });

    const toggle = (title: string) =>
        setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));

    return (
        <div className={cn('flex flex-col border-b', t.rule)}>
            {groups.map((group) => {
                const isOpen = openGroups[group.title] ?? false;
                return (
                    <div key={group.title} className={cn('border-t', t.rule)}>
                        <button
                            type="button"
                            onClick={() => toggle(group.title)}
                            aria-expanded={isOpen}
                            className="flex w-full items-center justify-between py-3.5 text-left"
                        >
                            <span
                                className={cn(
                                    'font-sans text-xl leading-none tracking-tight transition-opacity',
                                    t.text,
                                    isOpen ? 'opacity-100' : 'opacity-50',
                                )}
                            >
                                {group.title}
                            </span>
                            <span
                                className="relative size-6 shrink-0"
                                aria-hidden="true"
                            >
                                <span
                                    className={cn(
                                        'absolute top-1/2 left-1/2 h-px w-3.5 -translate-x-1/2 -translate-y-1/2',
                                        t.glyph,
                                    )}
                                />
                                {!isOpen && (
                                    <span
                                        className={cn(
                                            'absolute top-1/2 left-1/2 h-3.5 w-px -translate-x-1/2 -translate-y-1/2',
                                            t.glyph,
                                        )}
                                    />
                                )}
                            </span>
                        </button>
                        {isOpen && (
                            <div className="flex flex-col gap-2 pb-5">
                                {group.links.map((link) => (
                                    <RoccoLink
                                        key={link.label}
                                        to={link.to}
                                        newTab={link.newTab}
                                        isNonRemixRoute={link.isNonRemixRoute}
                                        className={cn(
                                            'text-sm leading-5 font-medium transition-opacity hover:opacity-70',
                                            link.icon &&
                                                'inline-flex items-center gap-2',
                                            t.text,
                                        )}
                                    >
                                        {link.label}
                                        {link.icon}
                                    </RoccoLink>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const BottomBar = ({
    t,
    us,
    termsLink,
    privacyLink,
}: {
    t: FooterThemeTokens;
    us: boolean;
    termsLink: string;
    privacyLink: string;
}) => (
    <div className="flex flex-col gap-6 pb-8 max-md:mt-8 md:mt-auto md:grid md:grid-cols-[1.6fr_1fr_1fr] md:items-end md:gap-[2.5vw] md:pb-10">
        <ArchiproWordmark
            aria-label="ArchiPro"
            className={cn('h-auto max-md:w-[55%] md:w-[19.64vw]', t.wordmark)}
        />
        <div className="flex items-end justify-between max-md:mt-6 md:contents">
            <p
                className={cn(
                    'm-0 text-sm leading-[1.3] tracking-[0.02em] md:pl-[2.5vw] md:text-base',
                    t.muted,
                )}
            >
                © ArchiPro {new Date().getFullYear()}
            </p>
            {/* Desktop only — on mobile these live in the Privacy accordion. */}
            <div
                className={cn(
                    'gap-5 text-sm leading-[1.3] tracking-[0.02em] max-md:hidden md:flex md:flex-col md:gap-2.5 md:pl-[2.5vw] md:text-base',
                    t.muted,
                )}
            >
                <RoccoLink
                    to={termsLink}
                    isNonRemixRoute
                    className="hover:opacity-70"
                >
                    Terms
                </RoccoLink>
                <RoccoLink
                    to={privacyLink}
                    isNonRemixRoute
                    className="hover:opacity-70"
                >
                    Privacy Policy
                </RoccoLink>
                {/* US notice-mode markets link to the CMS cookie-policy page
                    (BC-3174). */}
                {us && (
                    <RoccoLink
                        to="/cookie-policy"
                        isNonRemixRoute
                        className="hover:opacity-70"
                    >
                        Cookie Policy
                    </RoccoLink>
                )}
                {/* Opt-out link, every market. A Remix route, so it keeps
                    client-side navigation. */}
                <RoccoLink
                    to={PagesEnum.YOUR_PRIVACY_CHOICES}
                    className="inline-flex items-center gap-2 hover:opacity-70"
                >
                    {YOUR_PRIVACY_CHOICES_LABEL}
                    {us && <OptOutIcon />}
                </RoccoLink>
            </div>
        </div>
    </div>
);

export interface HomeFooterProps {
    /** Colour theme. Defaults to `dark`; `light` is supported for later opt-in. */
    theme?: FooterTheme;
}

export const HomeFooter = ({ theme = 'dark' }: HomeFooterProps) => {
    const { country, countryConfig } = useLocalisation();
    const t = THEME[theme];
    const us = isUS(country);
    const showLaunchCountdown = useProductLaunchCountdownEnabled();

    // Tell the floating island (project detail page) to hide once the footer is
    // in view — the legacy footer used to set this; the site-wide footer now
    // owns it (BC-4469, fixes "island never hides at footer").
    const setReachedFooter = useSetAtom(uiStateAtomReachedFooter);
    const { ref: footerRef, inView: footerInView } = useInView({
        rootMargin: '-120px',
    });
    useEffect(() => {
        setReachedFooter(footerInView);
        return () => setReachedFooter(false);
    }, [footerInView, setReachedFooter]);
    // Some routes (e.g. professional profiles) opt the Contact link out of the
    // footer via this handle, which the legacy footer honoured.
    const hideContactArchiPro = useMatchesHandleData<boolean>(
        'hideContactArchiPro',
        false,
    );
    const termsLink = getWebsiteTermsLink(
        country as Parameters<typeof getWebsiteTermsLink>[0],
    );
    const privacyLink = getPrivacyPolicyLink(
        country as Parameters<typeof getPrivacyPolicyLink>[0],
    );

    // US: drop Digital Magazines from Discover (BC-4427).
    const discover: FooterColumn = us
        ? {
              ...DISCOVER,
              links: DISCOVER.links.filter(
                  (link) => link.label !== 'Digital Magazines',
              ),
          }
        : DISCOVER;

    // Home Design Evening visibility mirrors the top nav.
    const connect: FooterColumn = countryConfig.showHDENavLink
        ? CONNECT
        : {
              ...CONNECT,
              links: CONNECT.links.filter(
                  (link) => link.label !== 'Home Design Evening',
              ),
          };

    const archipro: FooterColumn = hideContactArchiPro
        ? {
              ...ARCHIPRO,
              links: ARCHIPRO.links.filter((link) => link.label !== 'Contact'),
          }
        : ARCHIPRO;

    // Mobile-only "Privacy" group. The legal links sit in the bottom bar on
    // desktop, but on mobile that row is too cramped, so they collapse into an
    // accordion section under Connect instead, mirroring the bottom bar: Cookie
    // Policy is US-only, Your Privacy Choices shows in every market.
    const privacy: FooterColumn = {
        title: 'Privacy',
        links: [
            { label: 'Terms', to: termsLink, isNonRemixRoute: true },
            { label: 'Privacy Policy', to: privacyLink, isNonRemixRoute: true },
            ...(us
                ? [
                      {
                          label: 'Cookie Policy',
                          to: '/cookie-policy',
                          isNonRemixRoute: true,
                      },
                  ]
                : []),
            yourPrivacyChoicesLink(us),
        ],
    };

    // US keeps the slim Business column; NZ/AU mirror the legacy footer's full
    // "For Businesses" set (Why ArchiPro? / Business Solutions / Memberships /
    // Resources).
    const businessGroups: FooterColumn[] = us
        ? [businessColumn(country)]
        : businessGroupsForCountry(country);
    // Split the business groups across the two link columns so the rich NZ/AU
    // footer stays balanced; US (one slim group) keeps the original layout.
    const half = Math.ceil(businessGroups.length / 2);
    const column2Groups: FooterColumn[] = [
        discover,
        ...businessGroups.slice(0, half),
    ];
    const column3Groups: FooterColumn[] = [
        ...businessGroups.slice(half),
        archipro,
        connect,
    ];
    const accordionGroups: FooterColumn[] = [
        discover,
        ...businessGroups,
        archipro,
        connect,
        privacy,
    ];

    return (
        <footer ref={footerRef} className={cn('w-full', t.section)}>
            <div className="mx-auto flex w-full max-w-[1920px] flex-col px-6 pt-10 md:min-h-[min(56.51vw,1085px)] md:px-[2.76vw] md:pt-[9.375vw]">
                {/* Desktop: subscribe + link columns */}
                <div className="hidden flex-1 grid-cols-[1.6fr_1fr_1fr] md:grid md:gap-[2.5vw]">
                    <div className="flex flex-col">
                        <div className="flex max-w-[440px] flex-col gap-5">
                            <SubscribeBlock t={t} />
                        </div>
                        {/* US product-launch clock, low in the subscribe
                            column (Figma `4393:20992`). Anchored to the end of
                            the link-column band with mt-auto so the design's
                            bottom offset holds regardless of the subscribe
                            block's height; the margin is calibrated against
                            the US bottom bar (three legal links, one more
                            than the design frame). pt keeps a minimum gap
                            from the subscribe block when the columns run
                            short. */}
                        {showLaunchCountdown && (
                            <ProductLaunchCountdown className="mt-auto mb-[5.104vw] ml-[0.321vw] pt-10" />
                        )}
                    </div>
                    <div
                        className={cn(
                            'flex flex-col gap-14 md:border-l md:pl-[2.5vw]',
                            t.rule,
                        )}
                    >
                        {column2Groups.map((group) => (
                            <LinkColumn key={group.title} {...group} t={t} />
                        ))}
                    </div>
                    <div
                        className={cn(
                            'flex flex-col gap-14 md:border-l md:pl-[2.5vw]',
                            t.rule,
                        )}
                    >
                        {column3Groups.map((group) => (
                            <LinkColumn key={group.title} {...group} t={t} />
                        ))}
                    </div>
                </div>

                {/* Mobile: subscribe + accordion */}
                <div className="flex flex-col gap-5 md:hidden">
                    <SubscribeBlock t={t} />
                    <div className="mt-8">
                        <FooterAccordion groups={accordionGroups} t={t} />
                    </div>
                    {showLaunchCountdown && (
                        <ProductLaunchCountdown className="mt-10" />
                    )}
                </div>

                <BottomBar
                    t={t}
                    us={us}
                    termsLink={termsLink}
                    privacyLink={privacyLink}
                />
            </div>
        </footer>
    );
};
